/**
 * Contract general conditions (VERSO page) clauses.
 *
 * The default set mirrors the standard clauses historically printed on the
 * contract's verso. Each agency can override these from Settings; the override
 * is stored per-agency in `settings.contractClauses`, so data is fully isolated
 * between companies. When an agency has no custom config, the defaults below are
 * used automatically.
 *
 * A clause shape:
 *   { id, enabled, frTitle, fr, arTitle, ar }
 * `fr` / `ar` are plain text; use a leading "• " on a line for a bullet point.
 * Article numbering is rendered dynamically from the (reordered) position.
 */

export const DEFAULT_CLAUSES = [
  {
    id: 'objet', enabled: true,
    frTitle: 'Objet du contrat',
    fr: "Le présent contrat a pour objet la location d'un véhicule automobile destiné au transport de personnes par le locataire désigné au recto, qui accepte le véhicule en bon état de marche et de propreté.",
    arTitle: 'موضوع العقد',
    ar: 'يهدف هذا العقد إلى كراء سيارة لنقل الأشخاص للمكتري المذكور في الوجه، والذي يقبل السيارة في حالة جيدة من حيث التشغيل والنظافة.',
  },
  {
    id: 'duree', enabled: true,
    frTitle: 'Durée et restitution',
    fr: "La durée de la location est fixée au recto. Le véhicule doit être restitué à la date, l'heure et le lieu convenus, dans le même état de propreté et avec le même niveau de carburant qu'à la livraison. Tout retard sera facturé au tarif journalier en vigueur.",
    arTitle: 'المدة والإرجاع',
    ar: 'تحدد مدة الكراء في الوجه. يجب إرجاع السيارة في التاريخ والساعة والمكان المتفق عليه، في نفس حالة النظافة وبنفس مستوى الوقود المسلم. أي تأخير سيتم احتسابه بالسعر اليومي المعمول به.',
  },
  {
    id: 'conduite', enabled: true,
    frTitle: 'Conduite du véhicule',
    fr: "Le véhicule ne peut être conduit que par le locataire désigné au recto ou par les conducteurs additionnels mentionnés. Tous les conducteurs doivent être âgés de 21 ans minimum et titulaires d'un permis de conduire valide depuis au moins 2 ans.",
    arTitle: 'قيادة السيارة',
    ar: 'لا يمكن قيادة السيارة إلا من طرف المكتري المعين في الوجه أو من السائقين الإضافيين المذكورين. يجب أن يكون جميع السائقين لا يقل سنهم عن 21 سنة وحاصلين على رخصة سياقة سارية المفعول منذ سنتين على الأقل.',
  },
  {
    id: 'utilisation', enabled: true,
    frTitle: 'Utilisation',
    fr: "Le locataire s'engage à utiliser le véhicule en bon père de famille. Il est strictement interdit de:\n• Sous-louer ou prêter le véhicule à un tiers.\n• Transporter des matières dangereuses, inflammables ou illégales.\n• Participer à des compétitions, courses ou essais.\n• Utiliser le véhicule pour du transport rémunéré (taxi).\n• Conduire sous l'influence de l'alcool ou de stupéfiants.\n• Sortir du territoire marocain sans autorisation écrite.",
    arTitle: 'الاستعمال',
    ar: 'يلتزم المكتري باستعمال السيارة بحرص. يُمنع منعا كليا:\n• كراء أو إعارة السيارة لشخص آخر.\n• نقل مواد خطيرة أو قابلة للاشتعال أو غير قانونية.\n• المشاركة في السباقات أو التجارب.\n• استعمال السيارة للنقل مقابل أجر (طاكسي).\n• القيادة تحت تأثير الكحول أو المخدرات.\n• الخروج من التراب المغربي بدون إذن كتابي.',
  },
  {
    id: 'carburant', enabled: true,
    frTitle: 'Carburant',
    fr: "Le carburant est à la charge du locataire. Le véhicule est livré avec un certain niveau de carburant indiqué au recto et doit être restitué avec le même niveau. À défaut, la différence sera facturée au tarif en vigueur.",
    arTitle: 'الوقود',
    ar: 'الوقود على عاتق المكتري. تُسلم السيارة بمستوى وقود مذكور في الوجه ويجب إرجاعها بنفس المستوى. عند عدم احترام ذلك، سيتم احتساب الفرق بالسعر المعمول به.',
  },
  {
    id: 'caution', enabled: true,
    frTitle: 'Caution et paiement',
    fr: "Une caution est versée à la signature du contrat. Elle sera restituée intégralement au retour du véhicule en bon état. En cas de dommages, frais supplémentaires, infractions ou retard, le montant correspondant sera déduit de la caution.",
    arTitle: 'الضمان والأداء',
    ar: 'يُدفع ضمان عند التوقيع على العقد. يُسترجع كاملاً عند إرجاع السيارة في حالة جيدة. في حالة الأضرار أو المصاريف الإضافية أو المخالفات أو التأخير، سيتم اقتطاع المبلغ المقابل من الضمان.',
  },
  {
    id: 'accidents', enabled: true,
    frTitle: 'Accidents et sinistres',
    fr: "En cas d'accident, le locataire doit:\n• Aviser immédiatement la société dans les 24 heures.\n• Établir un constat amiable signé par les parties.\n• Déposer plainte si nécessaire auprès des autorités.\n• Ne jamais reconnaître sa responsabilité sur place.\nSans constat ou déclaration, le locataire sera tenu responsable de l'intégralité des dommages.",
    arTitle: 'الحوادث والأضرار',
    ar: 'في حالة وقوع حادثة، يجب على المكتري:\n• إخبار الشركة فوراً خلال 24 ساعة.\n• تحرير محضر ودي موقع من الأطراف.\n• تقديم شكاية لدى السلطات عند الضرورة.\n• عدم الاعتراف بالمسؤولية في عين المكان.\nبدون محضر أو تصريح، يتحمل المكتري مسؤولية جميع الأضرار.',
  },
  {
    id: 'assurance', enabled: true,
    frTitle: 'Assurance',
    fr: "Le véhicule est assuré tous risques. Néanmoins, le locataire reste responsable de la franchise en cas de sinistre responsable. Le bris de glaces, le vol des pneus, des accessoires et les dommages au bas de caisse ne sont pas couverts.",
    arTitle: 'التأمين',
    ar: 'السيارة مؤمنة ضد جميع الأخطار. مع ذلك، يبقى المكتري مسؤولا عن الفرنشيز في حالة كونه مسؤولا عن الحادث. كسر الزجاج، سرقة العجلات والإكسسوارات وأضرار أسفل السيارة غير مغطاة.',
  },
  {
    id: 'pannes', enabled: true,
    frTitle: 'Pannes et entretien',
    fr: "Le locataire doit vérifier régulièrement le niveau d'huile, d'eau et de la pression des pneus. En cas de panne, contacter immédiatement la société. Aucune réparation ne doit être effectuée sans accord écrit préalable.",
    arTitle: 'الأعطاب والصيانة',
    ar: 'يجب على المكتري التحقق بانتظام من مستوى الزيت والماء وضغط العجلات. في حالة العطب، يجب الاتصال فوراً بالشركة. لا تتم أي إصلاحات بدون موافقة كتابية مسبقة.',
  },
  {
    id: 'infractions', enabled: true,
    frTitle: 'Infractions',
    fr: "Toutes les infractions au code de la route (excès de vitesse, stationnement, etc.) commises pendant la durée de location sont à la charge exclusive du locataire, majorées de frais de gestion de 100 DH par infraction.",
    arTitle: 'المخالفات',
    ar: 'جميع مخالفات قانون السير (السرعة المفرطة، الوقوف، إلخ) المرتكبة خلال مدة الكراء على عاتق المكتري حصراً، مضاف إليها مصاريف تدبير 100 درهم لكل مخالفة.',
  },
  {
    id: 'resiliation', enabled: true,
    frTitle: 'Résiliation',
    fr: "La société se réserve le droit de résilier le contrat et de récupérer le véhicule à tout moment, sans préavis ni indemnité, en cas de manquement aux présentes conditions ou de comportement dangereux.",
    arTitle: 'الفسخ',
    ar: 'تحتفظ الشركة بحق فسخ العقد واسترجاع السيارة في أي وقت، بدون إشعار أو تعويض، في حالة الإخلال بالشروط أو السلوك الخطير.',
  },
  {
    id: 'litiges', enabled: true,
    frTitle: 'Litiges',
    fr: "Tout litige relatif à l'exécution du présent contrat sera soumis aux tribunaux compétents du Maroc, le droit marocain étant seul applicable.",
    arTitle: 'النزاعات',
    ar: 'كل نزاع متعلق بتنفيذ هذا العقد يخضع للمحاكم المختصة بالمغرب، ويطبق القانون المغربي وحده.',
  },
  {
    id: 'donnees', enabled: true,
    frTitle: 'Données personnelles',
    fr: "Les informations recueillies sont nécessaires à l'exécution du contrat et peuvent être conservées pour les besoins de la société conformément à la loi 09-08.",
    arTitle: 'المعطيات الشخصية',
    ar: 'المعلومات المجمعة ضرورية لتنفيذ العقد ويمكن الاحتفاظ بها لحاجيات الشركة طبقاً للقانون 09-08.',
  },
];

/** A deep clone of the defaults (so the editor can mutate freely). */
export const cloneDefaultClauses = () =>
  DEFAULT_CLAUSES.map(c => ({ ...c }));

/**
 * Return the ENABLED clauses for an agency: its custom set when configured,
 * otherwise the system defaults. Never mutates input.
 */
export const getActiveClauses = (settings) => {
  const custom = settings && Array.isArray(settings.contractClauses) ? settings.contractClauses : null;
  const list = (custom && custom.length) ? custom : DEFAULT_CLAUSES;
  return list.filter(c => c && c.enabled !== false && (c.fr || c.ar || c.frTitle || c.arTitle));
};

/**
 * Render one clause body (plain text → HTML). Lines beginning with "•" or "-"
 * become a bulleted list. Input is expected to be ALREADY HTML-escaped by the
 * caller (contractDoc escapes the whole settings object), so we only add markup.
 */
const renderBody = (text, rtl) => {
  const pad = rtl ? 'padding-right: 12px;' : 'padding-left: 12px;';
  const lines = String(text || '').split('\n').map(l => l.trim()).filter(Boolean);
  let html = '', inList = false;
  for (const line of lines) {
    const isBullet = /^[•\-]/.test(line);
    if (isBullet) {
      if (!inList) { html += `<ul style="${pad} margin-bottom: 4px;">`; inList = true; }
      html += `<li>${line.replace(/^[•\-]\s*/, '')}</li>`;
    } else {
      if (inList) { html += '</ul>'; inList = false; }
      html += `<p style="margin-bottom: 4px;">${line}</p>`;
    }
  }
  if (inList) html += '</ul>';
  return html;
};

/** Build the FR column body (article list) for the verso page. */
export const buildClausesHtmlFR = (clauses) =>
  clauses.map((c, i) =>
    `<p style="font-weight: 700; margin-bottom: 2px;">Article ${i + 1} - ${c.frTitle || ''}:</p>` +
    renderBody(c.fr, false)
  ).join('');

/** Build the AR column body (article list, RTL) for the verso page. */
export const buildClausesHtmlAR = (clauses) =>
  clauses.map((c, i) =>
    `<p style="font-weight: 700; margin-bottom: 2px;">الفصل ${i + 1} - ${c.arTitle || ''}:</p>` +
    renderBody(c.ar, true)
  ).join('');
